import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { collectPaginatedRows } from "../lib/paginatedRows";
import type { AppProfile } from "../types";

type CandidateStatus = "pending" | "shortlisted" | "rejected" | "mapped";
type CandidateConfidence = "high" | "medium" | "low";

type Candidate = {
  id: string;
  source_file: string;
  source_year: number | null;
  sheet_name: string | null;
  cell_ref: string | null;
  source_row: number | null;
  article_code: string | null;
  original_description: string | null;
  original_unit: string | null;
  candidate_kind: "resource_price" | "rendiment" | "subcontract_price";
  resource_family: string;
  resource_name: string | null;
  element: string | null;
  resource_spec: string | null;
  observed_quantity: number | null;
  observed_quantity_unit: string | null;
  observed_unit_price: number | null;
  observed_price_unit: string | null;
  origin: string | null;
  confidence: CandidateConfidence;
  flags: string | null;
  evidence: string | null;
  raw_comment: string | null;
  review_status: CandidateStatus;
  mapped_resource_id: string | null;
  staged_resource_price_id: string | null;
};

type CandidateStats = {
  total: number;
  pending: number;
  shortlisted: number;
  mapped: number;
  rejected: number;
  high_confidence: number;
  resource_prices: number;
  rendiments: number;
  subcontract_prices: number;
  staged_resource_prices: number;
};

type ResourceOption = {
  id: string;
  code: string;
  name: string;
  unit: string;
};

type Props = {
  profile: AppProfile;
};

function numberText(value: number | null, unit: string | null) {
  if (value === null || value === undefined) return "—";
  const text = new Intl.NumberFormat("pt-PT", {
    maximumFractionDigits: 4,
  }).format(Number(value));
  return unit ? text + " " + unit : text;
}

function priceText(value: number | null, unit: string | null) {
  if (value === null || value === undefined) return "—";
  const text = new Intl.NumberFormat("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(value));
  return unit ? text + " " + unit : text + " €";
}

export default function HistoricalNoteCandidates({ profile }: Props) {
  const canReview = profile.role === "admin" || profile.role === "estimator";
  const canImport = profile.role === "admin";

  const [rows, setRows] = useState<Candidate[]>([]);
  const [stats, setStats] = useState<CandidateStats | null>(null);
  const [resources, setResources] = useState<ResourceOption[]>([]);
  const [availableFamilies, setAvailableFamilies] = useState<string[]>([]);
  const loadGeneration = useRef(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<CandidateStatus | "all">("pending");
  const [confidence, setConfidence] = useState<CandidateConfidence | "all">("all");
  const [family, setFamily] = useState("all");
  const [busy, setBusy] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [mapping, setMapping] = useState<Record<string, string>>({});

  async function load() {
    const generation = ++loadGeneration.current;
    setBusy(true);
    setError("");

    const loadCandidates = (from: number, to: number) => {
      let request = supabase
        .from("historical_note_candidates")
        .select("id,source_file,source_year,sheet_name,cell_ref,source_row,article_code,original_description,original_unit,candidate_kind,resource_family,resource_name,element,resource_spec,observed_quantity,observed_quantity_unit,observed_unit_price,observed_price_unit,origin,confidence,flags,evidence,raw_comment,review_status,mapped_resource_id,staged_resource_price_id")
        .order("source_year", { ascending: false })
        .order("created_at", { ascending: false })
        .order("id", { ascending: true })
        .range(from, to);
      if (status !== "all") request = request.eq("review_status", status);
      if (confidence !== "all") request = request.eq("confidence", confidence);
      if (family !== "all") request = request.eq("resource_family", family);
      return request;
    };

    const [candidateResult, statResult, resourceResult, familyResult] = await Promise.all([
      collectPaginatedRows<Candidate>(loadCandidates),
      supabase.from("historical_note_candidate_stats").select("*").single(),
      supabase.from("resources").select("id,code,name,unit").eq("is_active", true).order("name", { ascending: true }),
      collectPaginatedRows<{ resource_family: string }>((from, to) =>
        supabase.from("historical_note_candidates").select("resource_family").order("id").range(from, to),
      ),
    ]);
    if (generation !== loadGeneration.current) return;
    if (!familyResult.error) setAvailableFamilies(Array.from(new Set((familyResult.data ?? []).map((row) => row.resource_family))).sort());

    if (candidateResult.error) {
      setError(candidateResult.error.message);
      setRows([]);
    } else {
      setRows((candidateResult.data ?? []) as Candidate[]);
    }

    if (!statResult.error) setStats(statResult.data as CandidateStats);
    if (!resourceResult.error) setResources((resourceResult.data ?? []) as ResourceOption[]);

    setBusy(false);
  }

  useEffect(() => {
    void load();
    return () => { loadGeneration.current += 1; };
  }, [status, confidence, family]);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-PT");
    if (!term) return rows;
    return rows.filter((row) =>
      [
        row.article_code,
        row.original_description,
        row.resource_name,
        row.resource_family,
        row.element,
        row.origin,
        row.evidence,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("pt-PT").includes(term)),
    );
  }, [rows, query]);

  const families = availableFamilies;

  async function importJson(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !canImport) return;

    setImporting(true);
    setError("");
    setNotice("");

    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed)) {
        throw new Error("O ficheiro JSON não contém uma lista de candidatos.");
      }

      let inserted = 0;
      let updated = 0;

      for (let index = 0; index < parsed.length; index += 200) {
        const chunk = parsed.slice(index, index + 200);
        const { data, error: rpcError } = await supabase.rpc(
          "import_historical_note_candidates",
          { payload: chunk },
        );
        if (rpcError) throw rpcError;
        const result = data as { inserted?: number; updated?: number } | null;
        inserted += Number(result?.inserted ?? 0);
        updated += Number(result?.updated ?? 0);
      }

      setNotice(
        `Importação concluída: ${inserted} novos candidatos e ${updated} atualizados. Nenhum preço foi aprovado automaticamente.`,
      );
      await load();
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : String(importError));
    } finally {
      setImporting(false);
    }
  }

  async function stagePrice(row: Candidate) {
    if (!canReview || row.staged_resource_price_id) return;

    setError("");
    setNotice("");

    const { data, error: rpcError } = await supabase.rpc(
      "stage_historical_note_resource_prices",
      { candidate_ids: [row.id] },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as { staged?: number; skipped_existing?: number } | null;
    setNotice(
      Number(result?.staged ?? 0) > 0
        ? "Preço histórico guardado no recurso como observação candidata. O preço atual L37 não foi alterado."
        : "Esta observação histórica já estava guardada no recurso.",
    );
    await load();
  }

  async function review(
    row: Candidate,
    nextStatus: CandidateStatus,
    resourceId: string | null = null,
  ) {
    if (!canReview) return;
    setError("");
    setNotice("");

    const { error: rpcError } = await supabase.rpc(
      "review_historical_note_candidate",
      {
        candidate_id: row.id,
        next_status: nextStatus,
        target_resource_id: resourceId,
      },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setNotice(
      nextStatus === "mapped"
        ? "Candidato ligado ao recurso. Continua sem alterar o preço atual."
        : "Estado do candidato atualizado.",
    );
    await load();
  }

  return (
    <section className="stack-lg">
      <div className="historical-candidate-intro card">
        <div>
          <p className="eyebrow">Notas dos Excel históricos</p>
          <h3>Preços & rendimentos extraídos</h3>
          <p className="muted">
            Fila de revisão. Estes valores vêm das notas/comentários das propostas
            antigas e nunca alteram automaticamente os preços dos Recursos.
          </p>
        </div>
        {canImport ? (
          <label className="button button-secondary historical-import-button">
            {importing ? "A importar…" : "Importar candidatos JSON"}
            <input
              accept=".json,application/json"
              disabled={importing}
              hidden
              type="file"
              onChange={importJson}
            />
          </label>
        ) : null}
      </div>

      {stats ? (
        <div className="historical-stats">
          <article className="card">
            <span>Total</span>
            <strong>{stats.total}</strong>
          </article>
          <article className="card">
            <span>Pendentes</span>
            <strong>{stats.pending}</strong>
          </article>
          <article className="card">
            <span>Alta confiança</span>
            <strong>{stats.high_confidence}</strong>
          </article>
          <article className="card">
            <span>Mapeados</span>
            <strong>{stats.mapped}</strong>
          </article>
          <article className="card">
            <span>No histórico</span>
            <strong>{stats.staged_resource_prices ?? 0}</strong>
          </article>
        </div>
      ) : null}

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <div className="historical-filters card">
        <label className="grow">
          <span>Pesquisar</span>
          <input
            placeholder="Artigo, descrição, fornecedor, recurso…"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label>
          <span>Estado</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as CandidateStatus | "all")}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="shortlisted">Pré-selecionados</option>
            <option value="mapped">Mapeados</option>
            <option value="rejected">Rejeitados</option>
          </select>
        </label>
        <label>
          <span>Confiança</span>
          <select
            value={confidence}
            onChange={(event) =>
              setConfidence(event.target.value as CandidateConfidence | "all")
            }
          >
            <option value="all">Todas</option>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>
        </label>
        <label>
          <span>Família</span>
          <select value={family} onChange={(event) => setFamily(event.target.value)}>
            <option value="all">Todas</option>
            {families.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      {busy ? (
        <div className="empty card">A carregar candidatos históricos…</div>
      ) : filtered.length === 0 ? (
        <div className="empty card">
          <strong>Ainda não existem candidatos neste filtro.</strong>
          <span>
            Quando importarmos a extração histórica, os preços e rendimentos
            aparecem aqui para revisão.
          </span>
        </div>
      ) : (
        <div className="historical-list">
          {filtered.map((row) => {
            const selectedResource = mapping[row.id] ?? row.mapped_resource_id ?? "";
            return (
              <article className="historical-candidate-card card" key={row.id}>
                <div className="historical-candidate-main">
                  <div className="result-meta">
                    <span className={"confidence-pill " + row.confidence}>
                      {row.confidence === "high"
                        ? "Alta"
                        : row.confidence === "medium"
                          ? "Média"
                          : "Baixa"}
                    </span>
                    <span>{row.source_year ?? "—"}</span>
                    <span>{row.candidate_kind}</span>
                    <span>{row.resource_family}</span>
                    {row.element ? <span>{row.element}</span> : null}
                  </div>

                  <h4>
                    {row.resource_name ||
                      row.original_description ||
                      row.article_code ||
                      "Candidato histórico"}
                  </h4>

                  <div className="historical-values">
                    <div>
                      <span>Preço observado</span>
                      <strong>{priceText(row.observed_unit_price, row.observed_price_unit)}</strong>
                    </div>
                    <div>
                      <span>Rendimento / quantidade</span>
                      <strong>
                        {numberText(row.observed_quantity, row.observed_quantity_unit)}
                      </strong>
                    </div>
                    <div>
                      <span>Origem</span>
                      <strong>{row.origin || "—"}</strong>
                    </div>
                  </div>

                  {row.evidence ? (
                    <p className="historical-evidence">{row.evidence}</p>
                  ) : null}
                  {row.raw_comment ? <details><summary>Nota original completa</summary><p style={{ whiteSpace: "pre-wrap" }}>{row.raw_comment}</p></details> : null}

                  <small className="muted">
                    {row.article_code ? row.article_code + " · " : ""}
                    {row.source_file.split("/").pop()}
                    {row.cell_ref ? " · " + row.cell_ref : ""}
                  </small>
                </div>

                {canReview ? (
                  <div className="historical-candidate-actions">
                    <button
                      className="button button-secondary"
                      type="button"
                      onClick={() => void review(row, "shortlisted")}
                    >
                      Pré-selecionar
                    </button>
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => void review(row, "rejected")}
                    >
                      Rejeitar
                    </button>

                    <label>
                      <span>Ligar a recurso</span>
                      <select
                        value={selectedResource}
                        onChange={(event) =>
                          setMapping((current) => ({
                            ...current,
                            [row.id]: event.target.value,
                          }))
                        }
                      >
                        <option value="">Selecionar…</option>
                        {resources.map((resource) => (
                          <option key={resource.id} value={resource.id}>
                            {resource.name} · {resource.unit}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className="button button-primary"
                      disabled={!selectedResource}
                      type="button"
                      onClick={() => void review(row, "mapped", selectedResource)}
                    >
                      Confirmar ligação
                    </button>

                    {row.review_status === "mapped" &&
                    row.mapped_resource_id &&
                    row.candidate_kind !== "rendiment" ? (
                      row.staged_resource_price_id ? (
                        <div className="historical-staged-badge">
                          Guardado no histórico do recurso
                        </div>
                      ) : (
                        <button
                          className="button button-secondary"
                          type="button"
                          onClick={() => void stagePrice(row)}
                        >
                          Guardar preço no histórico
                        </button>
                      )
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
